import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { Check } from 'lucide';
import { NbIcon } from '../../icon/icon';

export type WizardStepState = 'todo' | 'current' | 'done';

/**
 * `<nb-wizard-step>` — one step in a {@link NbWizardSteps} list (Mockup `.wizstep`).
 *
 * A numbered circle (`.n`) plus a title (`.t`). The `state` drives the look and
 * the semantics:
 *  - `todo`    — `--sunken` circle, muted title.
 *  - `current` — `--brand` circle on a `--brand`-on-`--on-brand` numeral, tinted
 *    `.on` row; the host gets `aria-current="step"`.
 *  - `done`    — `--success` circle showing a Check `<nb-icon>` (15px) in place of
 *    the number, so completion is never colour-only.
 *
 * Rendered as a `<li>` (set `role="listitem"` on the host) so it nests correctly
 * inside the parent `<ol role="list">`.
 *
 * @example
 * <nb-wizard-step [index]="2" state="current" title="Profil" />
 */
@Component({
  selector: 'nb-wizard-step',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon],
  host: {
    class: 'wizstep',
    role: 'listitem',
    '[class.on]': "state() === 'current'",
    '[class.done]': "state() === 'done'",
    '[attr.aria-current]': "state() === 'current' ? 'step' : null",
  },
  template: `<span class="n" aria-hidden="true">
      @if (state() === 'done') {
        <nb-icon [icon]="Check" />
      } @else {
        {{ index() }}
      }
    </span>
    <span class="t">{{ title() }}<span class="sr-only">{{ statusLabel() }}</span></span>`,
  styleUrl: './wizard.css',
})
export class NbWizardStep {
  /** The step's 1-based number, shown in the circle for todo/current steps. */
  readonly index = input.required<number>();
  /** Progress state — controls colour, the Check icon and `aria-current`. */
  readonly state = input<WizardStepState>('todo');
  /** The step's label, shown next to the circle. */
  readonly title = input<string>('');

  protected readonly Check = Check;

  /** Visually-hidden status suffix so state isn't conveyed by colour alone. */
  protected readonly statusLabel = computed(() => {
    switch (this.state()) {
      case 'done':
        return ' (fullført)';
      case 'current':
        return ' (gjeldende steg)';
      default:
        return '';
    }
  });
}
